import React, { useState, useRef, useCallback } from 'react';
import { Upload, File as FileIcon, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ALLOWED_EXT = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png', 'webp'];
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Upload component — drag&drop, real progress, 10MB cap.
 *
 * Uses XHR (not fetch) because fetch has no upload progress in browsers.
 * Direct multipart to backend → which streams to storage.
 *
 * Props:
 *   uploadUrl    : full URL or path
 *   docType      : sent in form-data
 *   label        : description shown to candidate
 *   accept       : "image/*,application/pdf,..."
 *   onUploaded   : ({filename, size, doc_type}) => void
 *   currentFiles : array of {filename, size} already uploaded
 *   multiple     : allow multiple uploads to same slot (otherwise replaces)
 */
const SmartUploader = ({
  uploadUrl,
  docType,
  label,
  helperText,
  accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.webp',
  onUploaded,
  currentFiles = [],
  multiple = false,
  required = false,
  testIdPrefix = '',
}) => {
  const [progress, setProgress] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const validateFile = (file) => {
    if (!file) return 'Aucun fichier';
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      return `Format non supporté (${ext}). Accepté : PDF, DOC, DOCX, JPG, PNG, WEBP`;
    }
    if (file.size > MAX_BYTES) {
      return `Fichier trop lourd (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum 10 MB.`;
    }
    if (file.size === 0) return 'Fichier vide';
    return null;
  };

  const upload = useCallback((file) => {
    const err = validateFile(file);
    if (err) {
      setError(err);
      toast.error(err);
      return;
    }
    setError(null);
    setUploading(true);
    setProgress(0);

    const xhr = new XMLHttpRequest();
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        setProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          toast.success(`${file.name} envoyé`);
          if (onUploaded) onUploaded(data);
          setProgress(100);
          setTimeout(() => setProgress(null), 700);
        } catch {
          setProgress(null);
        }
      } else {
        let msg = `Erreur ${xhr.status}`;
        try {
          const errBody = JSON.parse(xhr.responseText);
          if (errBody.detail) {
            const map = {
              file_too_large: 'Fichier trop lourd (max 10 MB)',
              unsupported_format: 'Format non supporté',
              invalid_doc_type: 'Type de document invalide',
              empty_file: 'Fichier vide',
              expired: 'Lien d\'onboarding expiré',
              not_found: 'Lien introuvable',
            };
            msg = map[errBody.detail] || errBody.detail;
          }
        } catch {}
        setError(msg);
        toast.error(msg);
        setProgress(null);
      }
    };
    xhr.onerror = () => {
      setUploading(false);
      setError('Connexion interrompue');
      toast.error('Connexion interrompue');
      setProgress(null);
    };
    xhr.open('POST', uploadUrl);
    xhr.send(fd);
  }, [uploadUrl, docType, onUploaded]);

  const handleFile = (file) => upload(file);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const onDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const onDragLeave = () => setDragOver(false);

  const hasFiles = currentFiles && currentFiles.length > 0;
  const canUploadMore = multiple || !hasFiles;

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}uploader`}>
      <div>
        <label className="text-sm font-medium text-tp-text">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {helperText && <p className="text-xs text-tp-text-light mt-0.5">{helperText}</p>}
      </div>

      {/* Existing files */}
      {hasFiles && (
        <div className="space-y-1.5">
          {currentFiles.map((f, i) => (
            <div key={`${f.filename}-${i}`} className="flex items-center gap-2 p-2 rounded bg-emerald-50 border border-emerald-200 text-xs">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
              <FileIcon className="h-3.5 w-3.5 text-tp-text-light flex-shrink-0" />
              <span className="flex-1 truncate text-emerald-900">{f.filename}</span>
              <span className="text-emerald-700 flex-shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
            </div>
          ))}
        </div>
      )}

      {canUploadMore && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`relative cursor-pointer rounded-lg border-2 border-dashed p-4 transition text-center ${
            dragOver ? 'border-tp-primary bg-tp-primary/10' : 'border-tp-border hover:border-tp-primary/40 bg-tp-bg/30'
          } ${uploading ? 'pointer-events-none opacity-80' : ''}`}
          data-testid={`${testIdPrefix}upload-dropzone`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden"
            data-testid={`${testIdPrefix}upload-input`}
          />
          {uploading ? (
            <div className="space-y-2">
              <Loader2 className="h-6 w-6 animate-spin text-tp-primary mx-auto" />
              <div className="w-full bg-tp-border rounded-full h-2 overflow-hidden">
                <div
                  className="bg-tp-primary h-full transition-all"
                  style={{ width: `${progress || 0}%` }}
                />
              </div>
              <p className="text-xs text-tp-text-light">{progress}% envoyé</p>
            </div>
          ) : (
            <div className="space-y-1">
              <Upload className="h-6 w-6 text-tp-text-light mx-auto" />
              <p className="text-sm font-medium text-tp-text">
                {hasFiles && multiple ? 'Ajouter un autre fichier' : 'Glisser un fichier ou cliquer'}
              </p>
              <p className="text-[11px] text-tp-text-light">
                PDF, DOC, DOCX, JPG, PNG · 10 MB max
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartUploader;
